//@flow
import * as React from 'react';
import { translate } from 'react-i18next';
import compose from 'recompose/compose';
import PjpConfigView from 'common/entity/pjpMaster/create/PjpConfigView';
import { PjPlanConfiguration } from 'common/models/internal/PjPlanConfiguration';
import { PJDistributionPlanConfiguration } from 'common/models/internal/PJDistributionPlanConfiguration';
import { connect } from 'react-redux';
import SchedulingConfigSelectors from 'common/redux/selectors/schedulingConfig';
import { IsFetchingVehicleModelsInProgress } from 'common/redux/selectors/vehicle';
import SchedulingConfigActions from 'common/redux/actions/schedulingConfig';
import VehicleActions from 'common/redux/actions/vehicle';
import SalesCategoriesActions from 'common/redux/actions/salesCategories';
import { isFetchClientInProgress } from 'common/redux/selectors/clientSelector';
import { clientIdSelector } from 'common/redux/selectors/sessionSelector';
import { withRouter } from 'common/components/core/router';
import PjpDashboardConfig from './PjpDashboardConfig';
import * as Immutable from 'immutable';
import { serializedSchedulingConfigFetchApi } from 'common/middleware/api';
import OverlayDialog from 'common/components/core/dialog/OverlayDialog';
import { Button, ButtonTypes } from 'common/components/core/button';
import DummyPersonaEditContainer from './DummyPersonaEditContainer';
import { getSalesCategories } from 'common/redux/selectors/salesCategories';
import { SalesCategoryGroup } from 'common/models/internal/SalesCategoryGroup';
import type { TFunction } from 'react-i18next';

import { OutletsData } from 'common/models/internal/OutletsData';
import type { DayShiftModelType } from 'common/models/flow/DayShift';
import type { ShiftMetadataModelType } from 'common/models/flow/ShiftMetadata';

type Props = {
    theme: Object,
    isLoadingSchedulingConfigs: boolean,
    isLoadingVehicleModels: boolean,
    isLoadingClient: boolean,
    errorMessage?: string,
    clientId: string,
    fetchVehicleModels: () => void,
    fetchSchedulingConfigs: () => void,
    fetchSalesCategories: Function,
    salesCategories: Array<any>,
    onNext: () => void,
    onPrev: () => void,
    pjpConfiguration: PjPlanConfiguration,
    onConfigurationChange: PjPlanConfiguration => void,
    outletsData: OutletsData,
    schedulingConfigFilters: () => void,
    t: TFunction,
    isIncrementalPlan: boolean
};

type SelectedDummyPersonaType = {
    workingDays: ?Array<DayShiftModelType>,
    holidays: ?Array<Object>,
    salesCategories: ?Array<string>,
    capacity: ?number,
    shiftMetadata: ?ShiftMetadataModelType
};

type State = {
    isEditDummyPersonaActive: boolean,
    selectedeDummyPersona: ?SelectedDummyPersonaType,
    selectedIndex: ?number
};

class PjpConfigContainer extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);
        this.state = {
            isEditDummyPersonaActive: false,
            selectedeDummyPersona: null,
            selectedIndex: null
        };
    }

    componentDidMount() {
        const {
            fetchVehicleModels,
            fetchSchedulingConfigs,
            fetchSalesCategories,
            clientId,
            outletsData,
            schedulingConfigFilters
        } = this.props;
        const { ids, request } = outletsData || {};
        fetchVehicleModels(clientId);
        fetchSchedulingConfigs(
            { clientId },
            { filters: schedulingConfigFilters() }
        );
        fetchSalesCategories(
            { clientId },
            {
                ids,
                request: request?.toJS()
            }
        );
    }
    componentDidUpdate(prevProps) {
        const { pjpConfiguration: prevPjpConfiguration } = prevProps;
        const {
            schedulingConfigId: prevSchedulingConfigId
        } = prevPjpConfiguration;
        const { pjpConfiguration } = this.props;
        const { schedulingConfigId } = pjpConfiguration;
        if (schedulingConfigId.configId) {
            if (
                schedulingConfigId.configId !== prevSchedulingConfigId.configId
            ) {
                this.getUpdatedConfigurations(
                    schedulingConfigId.configId,
                    pjpConfiguration
                );
            }
        }
    }

    openDummyPersonaModal = (): void => {
        this.setState({ isEditDummyPersonaActive: true });
    };

    closeDummyPersonaModal = (): void => {
        this.setState({
            isEditDummyPersonaActive: false,
            selectedIndex: null,
            selectedDummyPersona: null
        });
    };

    onDistributionPlanConfigChange = (
        distributionPlanConfiguration: PJDistributionPlanConfiguration
    ): void => {
        const { pjpConfiguration } = this.props;
        this.handlePjpConfigChange(
            pjpConfiguration.setDistributionPlanConfiguration(
                distributionPlanConfiguration
            )
        );
    };

    onTransportModeChange = (transportMode: string): void => {
        const { pjpConfiguration } = this.props;
        this.handlePjpConfigChange(
            pjpConfiguration.setTransportMode(transportMode)
        );
    };

    onToggleNeedsRevenueFairnessAcrossDays = (
        needsRevenueFairnessAcrossDays: boolean
    ): void => {
        const { pjpConfiguration } = this.props;
        this.handlePjpConfigChange(
            pjpConfiguration.setNeedsRevenueFairnessAcrossDays(
                needsRevenueFairnessAcrossDays
            )
        );
    };

    onToggleNeedsExistingBeatPlan = (needsExistingBeatPlan: boolean): void => {
        const { pjpConfiguration } = this.props;
        this.handlePjpConfigChange(
            pjpConfiguration.setNeedsExistingBeatPlan(needsExistingBeatPlan)
        );
    };

    onToggleMultiTrip = (needsMultiTrip: boolean): void => {
        const { pjpConfiguration } = this.props;
        this.handlePjpConfigChange(
            pjpConfiguration.setEnableMultiTrip(needsMultiTrip)
        );
    };

    getUpdatedConfigurations = (
        schedulingConfigId: string,
        updatedPjpConfiguration: PjPlanConfiguration
    ): void => {
        const { clientId, setDefaultSerializedConfig } = this.props;

        return serializedSchedulingConfigFetchApi
            .fetch({ clientId, configId: schedulingConfigId })
            .then(response => {
                const { serializedSchedulingConfig } = response;
                this.handlePjpConfigChange(
                    updatedPjpConfiguration.setSerializedSchedulingConfig(
                        serializedSchedulingConfig
                    )
                );
                setDefaultSerializedConfig(serializedSchedulingConfig);
            });
    };

    onSchedulingConfigIdChange = (schedulingConfigId: string): void => {
        const { pjpConfiguration } = this.props;
        const updatedSchedulingConfigId = pjpConfiguration.schedulingConfigId.setConfigId(
            schedulingConfigId
        );
        const updatedPjpConfiguration = pjpConfiguration.setSchedulingConfigId(
            updatedSchedulingConfigId
        );
        this.handlePjpConfigChange(updatedPjpConfiguration);
    };

    handlePjpConfigChange = (pjpConfiguration: PjPlanConfiguration) => {
        const { onConfigurationChange } = this.props;
        onConfigurationChange(pjpConfiguration);
    };

    onToggleExtraOutletInFrozenBeats = (
        isExtraOutletAllowedInFrozenBeats: boolean
    ) => {
        const { pjpConfiguration } = this.props;
        this.handlePjpConfigChange(
            pjpConfiguration.setIsExtraOutletAllowedInFrozenBeats(
                isExtraOutletAllowedInFrozenBeats
            )
        );
    };

    getSelectedSalesCategories = (
        selectedSalesCategories: Array<string>
    ): Array<string> => {
        const { pjpConfiguration } = this.props;
        const salesCategories = pjpConfiguration.getDefaultSalesCategoryOptions();
        const selectedSalesCategoriesObjects = selectedSalesCategories.map(
            salesCategoryValue =>
                salesCategories.find(
                    salesCategory => salesCategory.value === salesCategoryValue
                )
        );
        return selectedSalesCategoriesObjects;
    };

    editDummyPersona = (index: number): SelectedDummyPersonaType => {
        const {
            pjpConfiguration: {
                salesmanCategoryGroups,
                salesmanCategoryGroupCapacities,
                dummySalesmanProperties
            }
        } = this.props;
        const selectedSalesmanCategoryGroups = salesmanCategoryGroups.get(
            index
        );
        const selectedeSalesCategoryObject = this.getSelectedSalesCategories(
            selectedSalesmanCategoryGroups.categories
        );
        const selectedSalesmanCategoryGroupCapacities = salesmanCategoryGroupCapacities.get(
            index
        );
        const selectedDummySalesmanProperties = dummySalesmanProperties.get(
            index
        );
        const selectedDummyPersona = {
            salesCategories: selectedeSalesCategoryObject,
            capacity: selectedSalesmanCategoryGroupCapacities,
            workingDays: selectedDummySalesmanProperties.workingDays,
            holidays: selectedDummySalesmanProperties.holidays,
            shiftMetadata: selectedDummySalesmanProperties.shiftMetadata
        };
        this.setState({ selectedDummyPersona, selectedIndex: index });
        this.openDummyPersonaModal();
    };

    deleteDummyPersona = (index: number): void => {
        const {
            pjpConfiguration: {
                salesmanCategoryGroups,
                salesmanCategoryGroupCapacities,
                dummySalesmanProperties
            }
        } = this.props;
        const { pjpConfiguration } = this.props;
        const updatedSalesmanCategoryGroups = salesmanCategoryGroups.delete(
            index
        );
        const updatedSalesmanCategoryGroupCapacities = salesmanCategoryGroupCapacities.delete(
            index
        );
        const updatedDummySalesmanProperties = dummySalesmanProperties.delete(
            index
        );

        const updatedConfiguration = pjpConfiguration
            .setSalesmanCategoryGroups(updatedSalesmanCategoryGroups)
            .setSalesmanCategoryGroupCapacities(
                updatedSalesmanCategoryGroupCapacities
            )
            .setDummySalesmanProperties(updatedDummySalesmanProperties);

        this.handlePjpConfigChange(updatedConfiguration);
    };

    onAddDummyPersona = (): void => {
        const { pjpConfiguration } = this.props;
        const {
            salesmanCategoryGroups,
            salesmanCategoryGroupCapacities,
            dummySalesmanProperties
        } = pjpConfiguration;
        const index = salesmanCategoryGroups?.size || 0;
        const singleSalesCategoryGroup = SalesCategoryGroup.fromJS({
            categories: []
        });
        const updatedSalesmanCategoryGroups = salesmanCategoryGroups
            ? salesmanCategoryGroups.set(index, singleSalesCategoryGroup)
            : Immutable.Map().set(index, singleSalesCategoryGroup);

        const updatedSalesmanCategoryGroupCapacities = salesmanCategoryGroupCapacities
            ? salesmanCategoryGroupCapacities.set(index, undefined)
            : Immutable.Map().set(index, undefined);

        const updatedDummySalesmanProperties = dummySalesmanProperties
            ? dummySalesmanProperties.set(index, undefined)
            : Immutable.Map().set(index, undefined);

        this.handlePjpConfigChange(
            pjpConfiguration.mergeDeep({
                salesmanCategoryGroups: updatedSalesmanCategoryGroups,
                salesmanCategoryGroupCapacities: updatedSalesmanCategoryGroupCapacities,
                dummySalesmanProperties: updatedDummySalesmanProperties
            })
        );
        this.openDummyPersonaModal();
    };

    onToggleCanAddDummyBeats = (createDummies: boolean): void => {
        const { pjpConfiguration } = this.props;
        if (!createDummies) {
            this.handlePjpConfigChange(
                pjpConfiguration.mergeDeep({
                    createDummies,
                    dummySalesmanProperties: Immutable.Map()
                })
            );
        } else {
            this.handlePjpConfigChange(
                pjpConfiguration.mergeDeep({
                    createDummies
                })
            );
        }
    };

    onCategoryGroupChange = ({ categoryGroup, salesCategories }): void => {
        const { pjpConfiguration } = this.props;
        const { salesmanCategoryGroups } = pjpConfiguration;
        const updatedGroups = SalesCategoryGroup.fromJS({
            categories: salesCategories
        });

        const updatedSalesmanCategoryGroup = salesmanCategoryGroups.set(
            categoryGroup,
            updatedGroups
        );

        this.handlePjpConfigChange(
            pjpConfiguration.setSalesmanCategoryGroups(
                updatedSalesmanCategoryGroup
            )
        );
    };

    onCategoryGroupCapacityChange = ({ categoryGroup, capacity }): void => {
        const { pjpConfiguration } = this.props;
        const { salesmanCategoryGroupCapacities } = pjpConfiguration;
        this.handlePjpConfigChange(
            pjpConfiguration.setSalesmanCategoryGroupCapacities(
                salesmanCategoryGroupCapacities.set(categoryGroup, capacity)
            )
        );
    };

    onDeleteCategoryGroup = ({ categoryGroup }): void => {
        const { pjpConfiguration } = this.props;
        const { salesmanCategoryGroups } = pjpConfiguration;
        this.handlePjpConfigChange(
            pjpConfiguration.setSalesmanCategoryGroups(
                salesmanCategoryGroups.delete(categoryGroup)
            )
        );
    };

    onDummySalesmanPropertiesChange = ({
        updatedDummySalesmanProperties,
        categoryGroup
    }): void => {
        const { pjpConfiguration } = this.props;
        const { dummySalesmanProperties } = pjpConfiguration;
        this.handlePjpConfigChange(
            pjpConfiguration.setDummySalesmanProperties(
                dummySalesmanProperties.set(
                    categoryGroup,
                    updatedDummySalesmanProperties
                )
            )
        );
    };

    render() {
        const {
            t,
            pjpConfiguration,
            isLoadingSchedulingConfigs,
            isLoadingVehicleModels,
            isLoadingClient,
            onNext,
            onCancel,
            theme,
            errorMessage,
            onConfigurationChange,
            schedulingConfigs,
            onPrev,
            isIncrementalPlan
        } = this.props;
        const {
            isEditDummyPersonaActive,
            selectedDummyPersona,
            selectedIndex
        } = this.state;
        return (
            <>
                <PjpConfigView
                    schedulingConfigs={schedulingConfigs}
                    isIncrementalPlan={isIncrementalPlan}
                    deleteDummyPersona={this.deleteDummyPersona}
                    editDummyPersona={this.editDummyPersona}
                    onAddDummyPersona={this.onAddDummyPersona}
                    openDummyPersonaModal={this.openDummyPersonaModal}
                    closeDummyPersonaModal={this.closeDummyPersonaModal}
                    onCancel={onCancel}
                    onPrev={onPrev}
                    errorMessage={errorMessage}
                    noConfigError={
                        !schedulingConfigs?.length
                            ? t('pjpCreate.pjpConfig.noConfigError')
                            : null
                    }
                    onNext={onNext}
                    isLoading={
                        isLoadingSchedulingConfigs ||
                        isLoadingVehicleModels ||
                        isLoadingClient
                    }
                    pjpConfiguration={pjpConfiguration}
                    onDistributionPlanConfigChange={
                        this.onDistributionPlanConfigChange
                    }
                    onPjpConfigChange={this.handlePjpConfigChange}
                    onSchedulingConfigIdChange={this.onSchedulingConfigIdChange}
                    onTransportModeChange={this.onTransportModeChange}
                    onToggleNeedsRevenueFairnessAcrossDays={
                        this.onToggleNeedsRevenueFairnessAcrossDays
                    }
                    onToggleMultiTrip={this.onToggleMultiTrip}
                    onToggleNeedsExistingBeatPlan={
                        this.onToggleNeedsExistingBeatPlan
                    }
                    onToggleExtraOutletInFrozenBeats={
                        this.onToggleExtraOutletInFrozenBeats
                    }
                    onToggleCanAddDummyBeats={this.onToggleCanAddDummyBeats}
                    onCategoryGroupChange={this.onCategoryGroupChange}
                    onCategoryGroupCapacityChange={
                        this.onCategoryGroupCapacityChange
                    }
                    onDeleteCategoryGroup={this.onDeleteCategoryGroup}
                />

                {isEditDummyPersonaActive ? (
                    <OverlayDialog
                        active
                        className={theme.baseOverlay}
                        dialogClassName={theme.baseDialog}
                    >
                        <div className={theme.outerContainer}>
                            <Button
                                buttonType={ButtonTypes.Icon}
                                circle
                                icon="cancel"
                                className={theme.closeBtn}
                                theme={theme}
                                onClick={this.closeDummyPersonaModal}
                            />
                            <DummyPersonaEditContainer
                                deleteDummyPersona={this.deleteDummyPersona}
                                selectedIndex={selectedIndex}
                                selectedDummyPersona={selectedDummyPersona}
                                onPjpConfigChange={this.handlePjpConfigChange}
                                closeDummyPersonaModal={
                                    this.closeDummyPersonaModal
                                }
                                pjpConfiguration={pjpConfiguration}
                                t={t}
                                theme={theme}
                                salesCategories={pjpConfiguration.getSalesCategoryOptions()}
                            />
                        </div>
                    </OverlayDialog>
                ) : null}
                <PjpDashboardConfig
                    isLoadingSchedulingConfigs={isLoadingSchedulingConfigs}
                    getUpdatedConfig={this.getUpdatedConfigurations}
                    t={t}
                    changePjpConfig={onConfigurationChange}
                    theme={theme}
                    configuration={pjpConfiguration}
                />
            </>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const isLoadingSchedulingConfigs = SchedulingConfigSelectors.isEntitiesLoading(
        state,
        ownProps
    );
    const schedulingConfigs = SchedulingConfigSelectors.getSchedulingConfigForPjpCreation(
        state,
        ownProps
    );
    const isLoadingVehicleModels = IsFetchingVehicleModelsInProgress(
        state,
        ownProps
    );
    const salesCategories = getSalesCategories(state, ownProps);
    const clientId = clientIdSelector(state, ownProps);
    const isLoadingClient = isFetchClientInProgress(state, ownProps);

    return {
        isLoadingSchedulingConfigs,
        isLoadingVehicleModels,
        clientId,
        isLoadingClient,
        salesCategories,
        schedulingConfigs
    };
};

const withRedux = connect(mapStateToProps, {
    fetchSchedulingConfigs: SchedulingConfigActions.fetchEntities,
    fetchVehicleModels: VehicleActions.fetchVehicleModels,
    fetchSalesCategories: SalesCategoriesActions.fetchOutletSalesCategories
});

const ThemedPjpConfigContainer = compose(
    withRouter,
    withRedux,
    translate('entityManagement')
)(PjpConfigContainer);

export default ThemedPjpConfigContainer;
export {
    PjpConfigContainer as UndecoratedPjpConfigContainer,
    ThemedPjpConfigContainer as PjpConfigContainer
};
